import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import {Card} from "../../../components/card/card.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import {subWeeks} from "date-fns";
import ReportUtils from "../../../utils/report-utils.js";
import dispatchXrayService from "../../../services/reports/dispatch-xray-service.js";
import reportUtils from "../../../utils/report-utils.js";
import Content from "./components/content.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
const initialFilters = () => {
  const dateRef = new Date(new Date().setSeconds(0,0));
  return {
    dtStart: subWeeks(dateRef, 1),
    dtEnd: dateRef,
    nature: [],
    nature_group: [],
    team: [],
    street: [],
    place: [],
    type_place: [],
    neighborhood: []
  }
};

function buildPayload(filters){
  return {
    dtStart: reportUtils.formatDateTime(filters.dtStart),
    dtEnd: reportUtils.formatDateTime(filters.dtEnd),
    nature: JSON.stringify(filters.nature.map(el => el.value)),
    nature_group: JSON.stringify(filters.nature_group.map(el => el.value)),
    place: JSON.stringify(filters.place.map(el => el.value)),
    street: JSON.stringify(filters.street.map(el => el.value)),
    type_place: JSON.stringify(filters.type_place.map(el => el.value)),
    team: JSON.stringify(filters.team.map(el => el.value)),
    neighborhood: JSON.stringify(filters.neighborhood.map(el => el.value)),
  }
}

const createConfig = (filterLabels) => ({
  url: "/dispatch_x_ray/apply",
  filterLabels,

  buildFilters: (filters, utils) => ({
    dtStart: filters.dtStart ? utils.formatDateTime(filters.dtStart) : null,
    dtEnd: filters.dtEnd ? utils.formatDateTime(filters.dtEnd) : null,
    nature: JSON.stringify(filters.nature.map(el => el.value)),
    nature_group: JSON.stringify(filters.nature_group.map(el => el.value)),
    place: JSON.stringify(filters.place.map(el => el.value)),
    street: JSON.stringify(filters.street.map(el => el.value)),
    type_place: JSON.stringify(filters.type_place.map(el => el.value)),
    team: JSON.stringify(filters.team.map(el => el.value)),
    neighborhood: JSON.stringify(filters.neighborhood.map(el => el.value))
  }),

  buildAuditFilters: (filters, formatted, utils) =>
      utils.filters({
        ...filters,
        dtStart: formatted.dtStart,
        dtEnd: formatted.dtEnd
      }, filterLabels, true),

  validatePeriod: (filters) => ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, 'year')
});


const title = "Raio X de despachos";
const DispatchXRay = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [fixedTop, setFixedTop] = useState(null);
  const hasUrlFilters = () => {return ["dtStart", "dtEnd", 'team'].some(param => ReportUtils.getUrlParam(param))};
  const [filters, setFilters] = useState(() => ReportUtils.buildUrlFilters({initialFilters, includeTeam: true}));
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const printComponentRef = useRef(null);
  const buttonRef = useRef(null);
  const [headerSelected, setHeaderSelected] = useState(-1);

  const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    nature: "Natureza",
    nature_group: "Grupo de natureza",
    team: "Equipe",
    street: "Logradouro",
    place: "Local pré-cadastrado",
    type_place: "Tipo de local",
    neighborhood: "Bairro"
  };

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
      }, filterLabels)
    );

  }, [reportData]);

  const reportConfig = createConfig(filterLabels);

  useEffect(() => {
    if (!hasUrlFilters()) return;
    ReportUtils.fetchReport({currentFilters: filters, setLoading, setReportData, setCollapsed, ...reportConfig});
  }, []);

  const apply = async () => {
    if (hasUrlFilters()) await ReportUtils.fetchReport({currentFilters: filters, setLoading, setReportData, setCollapsed, ...reportConfig});

    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null)
      }
    });

    try {
      const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, "year");

      if (!periodValidation.ok) {
        ReportUtils.alertErrorMessage(periodValidation.message);
        setLoading(false);
        return;
      }

      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd},
        filterLabels, true);

      const data = await dispatchXrayService.apply({...buildPayload(filters), filters: auditFilters})
      setReportData(data);
      setAppliedFilters(filters);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {
      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);
    } finally {
      setLoading(false);
    }
  }

  const clearSearch = () => {
    ReportUtils.clearUrlFilters();
    setFilters({...initialFilters()});
    setReportData(null);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }


  return (
    <>
      <div className={"flex"}>

        <Template isLoading={loading}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={true}
                exportInSheets={true}
                printable={true}
                headerChange={true}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={reportData?.dispatchList?.length}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
                setCollapsed={setCollapsed}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>

            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${(!reportData || reportData.dispatchList.length === 0) ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.dispatchList.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${(!reportData || reportData.dispatchList.length === 0) ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.dispatchList?.length}/>
              <Content reportData={reportData} data={reportData?.dispatchList?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
            </div>
          </Card.Body>
        </Card>

      </div>
    </>
  );
};

export default DispatchXRay;